import { useMemo } from "react";

import { useAnalytics } from "@/analytics";
import { useAgentSdk, useCallbacksRemaining, usePartner } from "@/sdk";
import { useAgentStore } from "@/sdk/agent/store";
import { useLogger } from "@/sdk/logging/LoggerProvider";
import { endDynatraceAction, startDynatraceAction } from "@/utils/RealUserMonitoringClient";

import { useControlsStore } from "../CallControls/controls.store";
import { clearCallbacksState } from "../eventHandlers";
import { useCallbacksFormStore } from "./callbacksForm.store";

const { setPendingActivity } = useAgentStore.getState();

/**
 * Hook for scheduling callback logic to separate from UI components
 * TODO: This can use more refactoring in the future to separate SDK concerns better than the first pass
 */
export const useScheduleCallback = () => {
    const logger = useLogger({ module: "useScheduleCallback" });
    const partner = usePartner();

    const { canCallback } = useCallbacksRemaining();
    const agentSDK = useAgentSdk();
    const { dispatcher } = useAnalytics();
    const { closeCallbackPanel } = useControlsStore();
    const {
        callbackType,
        selectedCallbackNowDelay,
        selectedCallbackMdn,
        setCallbackScheduled,
        selectedCallbackOption,
    } = useCallbacksFormStore();

    const setCallbackScheduledSuccess = () => {
        setCallbackScheduled(true);

        void dispatcher
            .withMeta({ Client: partner })
            .withExtra({ isExternal: callbackType === "CallbackLater" })
            .dispatchBusinessEvent("CallbackScheduled");
        closeCallbackPanel();
    };

    const canScheduleOrUpdateCallback = useMemo(() => {
        if (!callbackType || !selectedCallbackMdn || !canCallback) return false; // if missing info
        if (
            // if valid callback now
            callbackType === "CallbackNow" &&
            (selectedCallbackNowDelay || selectedCallbackOption === "callback-user-now")
        )
            return true;
        return false;
    }, [callbackType, selectedCallbackOption, selectedCallbackMdn, selectedCallbackNowDelay, canCallback]);

    const scheduleCallback = async () => {
        if (callbackType === "CallbackNow") {
            startDynatraceAction("Callback");
            const success = await agentSDK.scheduleCallbackNow(selectedCallbackNowDelay, selectedCallbackMdn);
            endDynatraceAction("Callback", success ? undefined : { error: "CallbackFailed" });
            if (success) setCallbackScheduledSuccess();
        } else {
            logger.warn({ callbackType }, "Unhandled callback type");
        }
    };

    const cancelScheduledCallback = async () => {
        clearCallbacksState();
        if (agentSDK.serviceProvider === "connect") {
            const currentActivity = agentSDK.getAgentActivity();
            await agentSDK.setAgentActivity(currentActivity);
            setPendingActivity(undefined);
        }
    };

    return {
        scheduleCallback,
        /** whether a callback can currently be submitted or updated */
        canScheduleOrUpdateCallback,
        cancelScheduledCallback,
    };
};
